import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { FieldType } from "@regal-voice/shared-types";
import { Input } from "antd";
import isEmpty from "lodash/isEmpty";
import { useDispatch } from "react-redux";

import { RVFormFloatingLabel, RVFormItem, RVTypeaheadSelect } from "@regal-voice/ui";
import { JourneyFormContext } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/Contexts/JourneyFormContext";
import { useEventSchemaForTypeahead } from "ServerData/EventSchema/Hooks/useEventSchemaForTypeahead/useEventSchemaForTypeahead";
import { setFields } from "Services/state/journeys/JourneyStateSlice";

import { unflattenFields } from "../../Utils/utils";
import { ConditionalNodeDatePropertyValue } from "./components/ConditionalNodeDatePropertyValue";
import { ConditionalNodePropertyOperator } from "./components/ConditionalNodePropertyOperator";
import { ConditionalNodePropertyValue } from "./components/ConditionalNodePropertyValue";
import { internalIsDateComparison, operatorRequiresPropertyValue } from "./components/helpers";
import { ConditionalNodeOperator, conditionalOptions } from "./ConditionalNodeOperators";
import { PropertyFilterCriterionProps } from "./types";

import styles from "./ConditionalFormItems.module.scss";

const REQUIRED_VALUE = "Input value cannot be blank.";
const EQUALITY_OPERATOR_VALUE_REQUIRED = 'Input value cannot be blank. To match an empty string, enter "" as value.';

const EQUALITY_OPERATORS = ["equals", "notEquals"];

export function PropertyFilterCriterion({
    formValues,
    rootField,
    filterItem,
    isEventCondtional,
    isContactCondtional,
    isTriggeringEventConditional,
    parentConditionValues,
    autocompleteSchemas,
    triggeringEvent,
}: PropertyFilterCriterionProps): JSX.Element {
    // @todo - figure out why this needs to be asserted
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { formSubmited } = useContext(JourneyFormContext)!;

    const [isDateComparison, setIsDateComparison] = useState<boolean>(
        internalIsDateComparison(filterItem?.propertyOperator)
    );
    const dispatch = useDispatch();
    const [operatorNeedsPropertyValue, setOperatorNeedsPropertyValue] = useState<boolean>(
        operatorRequiresPropertyValue(filterItem?.propertyOperator)
    );
    const [availableOperators, setAvailableOperators] = useState<ConditionalNodeOperator[]>([]);

    const displayOperatorSelect = isEventCondtional || isTriggeringEventConditional || isContactCondtional;
    const propertyValueDisabled =
        (isEventCondtional && (!filterItem?.propertyName || !parentConditionValues?.eventName)) ||
        (isContactCondtional && !filterItem.propertyName);

    const propertyValueRequired = useCallback(
        async (_: any, propertyValue: string) => {
            const required = !!filterItem?.propertyName && filterItem?.propertyType != "object";
            //  isOneOf once has value property value is an array so validate against it
            const propertyDoesNotHaveValue = !propertyValue || isEmpty(propertyValue);
            if (formSubmited && required && propertyDoesNotHaveValue) {
                if (
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    EQUALITY_OPERATORS.includes(filterItem.propertyOperator!) &&
                    filterItem.propertyType == "string" &&
                    !isContactCondtional
                ) {
                    return Promise.reject(new Error(EQUALITY_OPERATOR_VALUE_REQUIRED));
                }
                return Promise.reject(new Error(REQUIRED_VALUE));
            }
            return Promise.resolve();
        },
        [formSubmited, isContactCondtional, filterItem]
    );

    const getPropertyNamePlaceHolder = useMemo(() => {
        let labelText: string | undefined = undefined;
        if (isContactCondtional) {
            labelText = "Contact Attribute";
        } else if (isEventCondtional || isTriggeringEventConditional) {
            labelText = "Property Name";
        }

        if (!labelText) {
            return;
        }
        return labelText;
    }, [isEventCondtional, isTriggeringEventConditional, isContactCondtional]);

    const validatePropertyName = useCallback(
        async (_: any, propertyName: any) => {
            if (!formSubmited) {
                return Promise.resolve();
            }
            if (!propertyName) {
                return Promise.reject(new Error("Property Name is required."));
            }
            return Promise.resolve();
        },
        [formSubmited]
    );

    useEffect(() => {
        setIsDateComparison(internalIsDateComparison(filterItem?.propertyOperator));
        setOperatorNeedsPropertyValue(operatorRequiresPropertyValue(filterItem?.propertyOperator));
    }, [filterItem?.propertyOperator]);

    useEffect(() => {
        if (isEventCondtional || isTriggeringEventConditional) {
            const attribute = filterItem.propertyName;
            let propertyType: FieldType | undefined;
            if (attribute !== undefined && attribute in autocompleteSchemas?.eventSchema) {
                propertyType = autocompleteSchemas?.eventSchema[attribute].fieldType;
            }
            const operators = conditionalOptions(parentConditionValues.conditionalType, attribute, propertyType);
            setAvailableOperators(operators);
        }
    }, [
        filterItem.propertyName,
        autocompleteSchemas?.eventSchema,
        isEventCondtional,
        isTriggeringEventConditional,
        parentConditionValues.conditionalType,
    ]);

    useEffect(() => {
        if (isContactCondtional) {
            const attribute = filterItem.propertyName;
            let propertyType: FieldType | undefined;
            if (attribute !== undefined && attribute in autocompleteSchemas?.contactAttributes) {
                propertyType = autocompleteSchemas?.contactAttributes[attribute].fieldType;
            }
            const operators = conditionalOptions(parentConditionValues.conditionalType, attribute, propertyType);
            setAvailableOperators(operators);
        }
    }, [
        filterItem.propertyName,
        autocompleteSchemas?.contactAttributes,
        isContactCondtional,
        parentConditionValues.conditionalType,
    ]);

    function onPropertyNameChange(propertyName: string) {
        const updatedFilters = [...formValues.filters];
        updatedFilters.splice(rootField.name, 1, { propertyName, propertyOperator: "isSet" });
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    filters: updatedFilters,
                })
            )
        );
    }

    const propertyNameSchema = isContactCondtional
        ? autocompleteSchemas?.contactAttributes
        : autocompleteSchemas?.eventSchema;
    const propertyNameTypeahead = useEventSchemaForTypeahead({
        eventSchema: propertyNameSchema,
        onChange: (value) => onPropertyNameChange(value),
    });

    return (
        <>
            <RVFormItem
                {...rootField}
                name={[rootField.name, "propertyType"]}
                noStyle
                key={`propertyType-${rootField ? rootField.name : 1}`}
            >
                <Input type={"hidden"} />
            </RVFormItem>
            <RVFormItem
                {...rootField}
                noStyle
                name={[rootField.name, "propertyFieldId"]}
                key={`propertyFieldId-${rootField ? rootField.name : 1}`}
            >
                <Input type={"hidden"} />
            </RVFormItem>
            {(isEventCondtional || isTriggeringEventConditional || isContactCondtional) && (
                <>
                    <RVFormFloatingLabel
                        name={["filters", rootField.name, "propertyName"]}
                        key={`floating-propertyName-${rootField ? rootField.name : 1}`}
                    >
                        <RVFormItem
                            {...rootField}
                            key={`propertyName-${rootField ? rootField.name : 1}`}
                            name={[rootField.name, "propertyName"]}
                            className={styles.formItem}
                            dependencies={["conditionalType", "eventName", [rootField.name, "propertyOperator"]]}
                            rules={[
                                {
                                    validator: validatePropertyName,
                                    message: "Property Name required or remove condition.",
                                },
                            ]}
                            label={rootField ? getPropertyNamePlaceHolder : undefined}
                        >
                            <RVTypeaheadSelect
                                {...propertyNameTypeahead}
                                onChange={(value) => onPropertyNameChange(value)}
                                placeholder={!rootField ? getPropertyNamePlaceHolder : undefined}
                                dataTestid={`filters-${rootField.name}-propertyName`}
                            />
                        </RVFormItem>
                    </RVFormFloatingLabel>
                </>
            )}

            {displayOperatorSelect && (
                <ConditionalNodePropertyOperator
                    formValues={formValues}
                    isContactCondtional={isContactCondtional}
                    isEventConditional={isEventCondtional || isTriggeringEventConditional}
                    propertyOperator={filterItem?.propertyOperator}
                    rootField={rootField}
                    availableOperators={availableOperators}
                    contactAttributes={autocompleteSchemas?.contactAttributes}
                    eventSchema={autocompleteSchemas?.eventSchema}
                />
            )}
            {operatorNeedsPropertyValue && (
                <ConditionalNodePropertyValue
                    rootField={rootField}
                    propertyType={filterItem?.propertyType}
                    propertyOperator={filterItem?.propertyOperator}
                    isContactCondtional={isContactCondtional}
                    propertyFieldId={filterItem?.propertyFieldId}
                    propertyValueDisabled={propertyValueDisabled}
                    propertyValueRequired={propertyValueRequired}
                    triggeringEvent={triggeringEvent}
                />
            )}
            {isDateComparison && (
                <ConditionalNodeDatePropertyValue
                    formValues={formValues}
                    rootField={rootField}
                    propertyOperator={filterItem?.propertyOperator}
                    propertyValueDisabled={propertyValueDisabled}
                    propertyFieldId={filterItem?.propertyFieldId}
                    propertyValueRequired={propertyValueRequired}
                    isContactCondtional={isContactCondtional}
                    dateComparisonType={filterItem?.dateComparisonType}
                    relativeDateValue={filterItem?.relativeDateValue}
                    triggeringEvent={triggeringEvent}
                />
            )}
        </>
    );
}
